<div fxLayout="column" fxFlex fxLayoutGap="24px">
  <!-- Header -->
  <div fxLayout="row" fxLayoutAlign="space-between center">
    <h2>Generate Issuance Offer</h2>
    <button mat-icon-button [routerLink]="['../']" matTooltip="Back to Issuance">
      <mat-icon>arrow_back</mat-icon>
    </button>
  </div>

  <!-- Loading State -->
  @if (loading) {
    <div fxLayout="row" fxLayoutAlign="center center" class="loading-container">
      <mat-spinner diameter="40"></mat-spinner>
      <span>Loading configurations...</span>
    </div>
  }

  <!-- Main Content -->
  @if (!loading) {
    <!-- Form Section -->
    <form [formGroup]="form" (ngSubmit)="onSubmit()" fxLayout="column" fxLayoutGap="24px">
      <!-- Configuration Selection Card -->
      <mat-card class="form-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>settings</mat-icon>
            Offer Configuration
          </mat-card-title>
          <mat-card-subtitle>
            Select an issuance configuration to generate an offer
          </mat-card-subtitle>
        </mat-card-header>
        <mat-card-content fxLayout="column" fxLayoutGap="16px">
          <mat-form-field>
            <mat-select formControlName="flow">
              <mat-label>Flow</mat-label>
              <mat-option value="authorization_code">Authorization Code</mat-option>
              <mat-option value="pre_authorized_code">Pre Authorized Code</mat-option>
            </mat-select>
            <mat-hint>Select flow for the issuance offer</mat-hint>
          </mat-form-field>

          <!-- TX Code (visible only for pre-authorized flow) -->
          @if (form.get('flow')?.value === 'pre_authorized_code') {
            <mat-form-field>
              <mat-label>Transaction Code (Optional)</mat-label>
              <input matInput formControlName="tx_code" placeholder="Enter transaction code" />
              <mat-icon matSuffix>password</mat-icon>
              <mat-hint>Optional transaction code for pre-authorized code flow</mat-hint>
            </mat-form-field>
          }

          <!-- Credential Configuration IDs -->
          <mat-form-field>
            <mat-label>Credential Configuration IDs</mat-label>
            <mat-select formControlName="credentialConfigurationIds" multiple>
              @for (binding of credentialConfigs; track binding.id) {
                <mat-option [value]="binding.id">
                  {{ binding.id }}
                </mat-option>
              }
            </mat-select>
            <mat-icon matSuffix>badge</mat-icon>
            <mat-hint
              >Leave empty to use all configurations from the selected issuance config</mat-hint
            >
          </mat-form-field>
        </mat-card-content>
      </mat-card>
      @if (fields.length > 0) {
        <mat-card>
          <mat-card-header>
            <mat-card-title>
              <mat-icon>assignment</mat-icon>
              Credential Claims Configuration
            </mat-card-title>
            <mat-card-subtitle> Configure the source for credential claims data </mat-card-subtitle>
          </mat-card-header>
          <mat-card-content fxLayout="column" fxLayoutGap="24px">
            @for (element of elements; track element.id; let i = $index) {
              <div class="credential-section" fxLayout="column" fxLayoutGap="16px">
                <!-- Credential Header -->
                <div class="credential-header" fxLayout="row" fxLayoutAlign="space-between center">
                  <h3 class="credential-title">
                    <mat-icon>badge</mat-icon>
                    {{ element.id }}
                  </h3>
                </div>

                <mat-divider></mat-divider>

                <!-- Claim Source Selection -->
                <div class="claim-source-selection">
                  <b class="source-label">Select claims data source:</b>
                  <mat-radio-group
                    [(ngModel)]="element.claimSource"
                    (change)="onClaimSourceChange(element.id, element.claimSource)"
                    [ngModelOptions]="{ standalone: true }"
                    fxLayout="row"
                    fxLayoutGap="16px"
                  >
                    <mat-radio-button value="form">
                      <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="8px">
                        <mat-icon>edit_note</mat-icon>
                        <span>Form Input</span>
                      </div>
                    </mat-radio-button>
                    <mat-radio-button value="webhook" [disabled]="!element.claimsWebhook?.url">
                      <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="8px">
                        <mat-icon>webhook</mat-icon>
                        <span>Webhook</span>
                      </div>
                    </mat-radio-button>
                  </mat-radio-group>

                  <!-- Help text based on availability -->
                  @if (!element.claimsWebhook?.url) {
                    <p class="help-text info">
                      <mat-icon>info</mat-icon>
                      No webhook configured - form input will be used
                    </p>
                  }
                </div>

                <!-- Content based on selected source -->
                @if (element.claimSource === 'webhook' && element.claimsWebhook?.url) {
                  <div class="webhook-info">
                    <app-webhook-config-show
                      [config]="element.claimsWebhook"
                      title="Claims"
                      descriptions="to fetch claims during issuance"
                    ></app-webhook-config-show>
                    <p class="help-text">
                      <mat-icon>info</mat-icon>
                      Claims will be fetched from the configured webhook during issuance
                    </p>
                  </div>
                } @else if (element.claimSource === 'form') {
                  <div class="form-input">
                    <formly-form
                      [form]="getForm(element.id)"
                      [fields]="getFields(fields[i])"
                      [model]="model"
                    ></formly-form>
                    @if (element.claims) {
                      <button
                        mat-button
                        type="button"
                        (click)="addPreConfigured(element)"
                        class="pre-config-button"
                      >
                        <mat-icon>auto_fix_high</mat-icon>
                        Use Pre-configured Default Values
                      </button>
                    }
                  </div>
                }
              </div>

              @if (i < elements.length - 1) {
                <mat-divider></mat-divider>
              }
            }
          </mat-card-content>
          <mat-card-actions fxLayout="row" fxLayoutAlign="end center">
            <button mat-button type="button" (click)="importClaims()">
              <mat-icon>upload_file</mat-icon>
              Import Claims JSON
            </button>
          </mat-card-actions>
        </mat-card>
      }

      <mat-divider></mat-divider>

      <div fxLayout="row" fxLayoutAlign="end center">
        <button mat-button type="button" (click)="resetForm()">Reset</button>
        <button
          mat-flat-button
          color="primary"
          type="submit"
          [disabled]="form.invalid || generatingOffer"
        >
          Generate Offer
        </button>
      </div>
    </form>
  }
</div>
